import React, { useState, useEffect } from "react"
import styles from "./index.module.scss"
import classnames from "classnames"
import {
  handleProfileImageError,
  resolveProfileImage,
} from "../../../helpers/profileImage"
import {
  notificationMessageDecoder,
  notificationTitleDecoder,
} from "../../../helpers/messages/notificationsDecoder"
import { getNotificationDate } from "../../../helpers/dateHandlers"
import handleViewport from "react-in-viewport"
import communityServices from "../../../services/community"
import prospectsServices from "../../../services/prospectsServices"

const NotificationCard = ({
  image,
  isNew,
  darkCard,
  date,
  notification,
  forwardedRef,
  onEnterViewport,
  onStatusChange,
}) => {
  const formatedDate = getNotificationDate(date)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isAccepted, setIsAccepted] = useState(
    notification.invite_status === "accepted"
  )
  const [isDeclined, setIsDeclined] = useState(
    notification.invite_status === "rejected"
  )

  const isPeerCircleInvitation =
    notification.body === "you_received_a_new_peer_circle_invitation" &&
    notification.attached_object?.type === "question"

  const isOpportunityInvitation =
    notification.body === "opportunity_created_invited" &&
    notification.attached_object?.type === "opportunity"

  useEffect(() => {
    const checkProspectStatus = async () => {
      if (isOpportunityInvitation && notification.attached_object?.uuid) {
        try {
          const opportunityUuid = notification.attached_object.uuid
          
          // Check localStorage first for persisted status
          const storageKey = `prospect_status_${opportunityUuid}`
          const storedStatus = localStorage.getItem(storageKey)
          if (storedStatus === "accepted") {
            setIsAccepted(true)
            setIsDeclined(false)
            return
          } else if (storedStatus === "rejected") {
            setIsDeclined(true)
            setIsAccepted(false)
            return
          }
          
          // If not in localStorage, check from API
          const response = await prospectsServices.getMyProspects("")
          
          // Handle different response structures
          let prospects = []
          if (response?.data?.prospects) {
            prospects = response.data.prospects
          } else if (Array.isArray(response?.data)) {
            prospects = response.data
          } else if (response?.prospects) {
            prospects = response.prospects
          }
          
          // Find prospect for this opportunity
          const prospect = prospects.find(
            (p) => p.opportunity?.uuid === opportunityUuid || p.opportunity_uuid === opportunityUuid
          )
          
          if (prospect) {
            const status = prospect.status
            // Check for accepted status (string "accepted" or enum value 1)
            if (status === "accepted" || status === 1 || status === "1") {
              setIsAccepted(true)
              setIsDeclined(false)
              // Persist to localStorage
              localStorage.setItem(storageKey, "accepted")
            } 
            // Check for rejected status (string "rejected" or enum value 2)
            else if (status === "rejected" || status === 2 || status === "2") {
              setIsDeclined(true)
              setIsAccepted(false)
              // Persist to localStorage
              localStorage.setItem(storageKey, "rejected")
            }
          }
        } catch (error) {
          console.error("Error checking prospect status:", error)
        }
      }
    }

    checkProspectStatus()
  }, [isOpportunityInvitation, notification.attached_object?.uuid])

  const handleAccept = async () => {
    if (isProcessing || isAccepted || isDeclined) return
    if (!notification.attached_object?.uuid) return

    setIsProcessing(true)
    try {
      if (isPeerCircleInvitation) {
        await communityServices.acceptPeerCircleInvitation(
          notification.attached_object.uuid
        )
      } else if (isOpportunityInvitation) {
        await handleOpportunityAccept()
        // Persist status to localStorage
        const opportunityUuid = notification.attached_object?.uuid
        if (opportunityUuid) {
          localStorage.setItem(`prospect_status_${opportunityUuid}`, "accepted")
        }
      }
      setIsAccepted(true)
      if (onStatusChange) {
        onStatusChange()
      }
      if (onEnterViewport) {
        onEnterViewport()
      }
    } catch (error) {
      console.error("Error accepting invitation:", error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDecline = async () => {
    if (isProcessing || isAccepted || isDeclined) return
    if (!notification.attached_object?.uuid) return

    setIsProcessing(true)
    try {
      if (isPeerCircleInvitation) {
        await communityServices.declinePeerCircleInvitation(
          notification.attached_object.uuid
        )
      } else if (isOpportunityInvitation) {
        await handleOpportunityDecline()
        // Persist status to localStorage
        const opportunityUuid = notification.attached_object?.uuid
        if (opportunityUuid) {
          localStorage.setItem(`prospect_status_${opportunityUuid}`, "rejected")
        }
      }
      setIsDeclined(true)
      if (onStatusChange) {
        onStatusChange()
      }
      if (onEnterViewport) {
        onEnterViewport()
      }
    } catch (error) {
      console.error("Error declining invitation:", error)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleOpportunityAccept = async () => {
    if (!notification.attached_object?.uuid) return
    
    try {
      const opportunityUuid = notification.attached_object.uuid
      const response = await prospectsServices.getMyProspects("")
      
      if (response?.data?.prospects && response.data.prospects.length > 0) {
        const prospect = response.data.prospects.find(
          (p) => p.opportunity?.uuid === opportunityUuid && (p.status === "requested" || p.status === 3)
        )
        
        if (prospect) {
          await prospectsServices.changeProspectStatus(prospect.uuid, "accepted")
        } else {
          throw new Error("Prospect not found for this opportunity")
        }
      } else {
        throw new Error("No prospects found")
      }
    } catch (error) {
      console.error("Error accepting opportunity invitation:", error)
      throw error
    }
  }

  const handleOpportunityDecline = async () => {
    if (!notification.attached_object?.uuid) return
    
    try {
      const opportunityUuid = notification.attached_object.uuid
      const response = await prospectsServices.getMyProspects("")
      
      if (response?.data?.prospects && response.data.prospects.length > 0) {
        const prospect = response.data.prospects.find(
          (p) => p.opportunity?.uuid === opportunityUuid && (p.status === "requested" || p.status === 3)
        )
        
        if (prospect) {
          await prospectsServices.changeProspectStatus(prospect.uuid, "rejected")
        } else {
          throw new Error("Prospect not found for this opportunity")
        }
      } else {
        throw new Error("No prospects found")
      }
    } catch (error) {
      console.error("Error declining opportunity invitation:", error)
      throw error
    }
  }

  return (
    <div
      className={classnames(styles.cardWrapper, {
        [styles.newCard]: isNew && !darkCard,
        [styles.darkCard]: darkCard,
        [styles.newDarkCard]: darkCard && isNew,
      })}
      ref={forwardedRef}
    >
      <div className={styles.mainInfo}>
        <img
          className={styles.userImage}
          src={resolveProfileImage(image)}
          onError={handleProfileImageError}
          alt=""
        />
        <div className={styles.textContent}>
          <h2 className={styles.action}>
            {notificationTitleDecoder(notification)}
          </h2>
          <div className={styles.message}>
            {notificationMessageDecoder(notification)}
          </div>
          {(isPeerCircleInvitation || isOpportunityInvitation) && !isAccepted && !isDeclined && (
            <div className={styles.actionButtons}>
              <button
                className={styles.acceptButton}
                onClick={handleAccept}
                disabled={isProcessing}
              >
                Accept
              </button>
              <span className={styles.orText}>or</span>
              <button
                className={styles.declineButton}
                onClick={handleDecline}
                disabled={isProcessing}
              >
                Decline
              </button>
              <span className={styles.invitationText}>invitation</span>
            </div>
          )}
          {(isAccepted || isDeclined) && (
            <div className={styles.statusText}>
              {isAccepted ? "Invitation accepted" : "Invitation declined"}
            </div>
          )}
        </div>
      </div>
      <div className={styles.date}>{formatedDate}</div>
    </div>
  )
}

export default handleViewport(NotificationCard)
